<!--
  Drawer de sincronizacion (Paso 8 / Fase 6, tareas 6.2 y 6.3).
  Lista todas las operaciones de la outbox que no estan synced, con su entidad,
  cuando se crearon, su estado y el mensaje de error/rechazo del server. Permite
  reintentar (error/conflict) y descartar (limpia tambien la fila optimista del
  cache). Se abre desde el badge (PendingSyncBadge) via el store syncUi.
-->
<script>
    import { liveQuery } from "dexie";
    import { fly, fade } from "svelte/transition";
    import { outboxDb } from "$lib/outbox/outboxDb";
    import { syncUi } from "$lib/stores/syncUi.svelte.js";
    import { isOnline } from "$lib/sync/networkStatus";
    import {
        drainOutbox,
        retryOperation,
        discardOperation,
    } from "$lib/sync/drainer";

    // Entidad del batch -> etiqueta legible para el operario.
    const ENTITY_LABEL = {
        comment: "Observación",
        weight: "Pesaje",
        treatmentEvent: "Tratamiento",
        pregnancyCheck: "Tacto",
        birth: "Nacimiento",
        service: "Servicio",
    };

    // Estado de la fila -> etiqueta + clases del chip.
    const STATUS_META = {
        pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
        in_flight: { label: "Enviando…", cls: "bg-sky-100 text-sky-800" },
        error: { label: "Error", cls: "bg-cf-danger-soft text-cf-danger" },
        conflict: { label: "Rechazado", cls: "bg-cf-danger-soft text-cf-danger" },
    };

    const rows = liveQuery(async () => {
        const r = await outboxDb.outbox
            .where("status")
            .anyOf(["pending", "in_flight", "error", "conflict"])
            .sortBy("createdAt");
        return r.reverse(); // mas nuevo arriba
    });

    function hace(ts) {
        if (!ts) return "";
        const s = Math.floor((Date.now() - ts) / 1000);
        if (s < 60) return "recién";
        const m = Math.floor(s / 60);
        if (m < 60) return `hace ${m} min`;
        const h = Math.floor(m / 60);
        if (h < 24) return `hace ${h} h`;
        const d = Math.floor(h / 24);
        return `hace ${d} día${d === 1 ? "" : "s"}`;
    }

    function entityLabel(e) {
        return ENTITY_LABEL[e] ?? e;
    }

    // Traduce errores tecnicos a algo que el operario entienda. Si no matchea
    // ningun patron conocido, cae al mensaje crudo (mejor algo que nada).
    function humanizeError(raw, errorCode) {
        // Tope del plan: el mensaje del backend ya dice el plan y el máximo, así
        // que se conserva y solo se aclara que el dato sigue acá. Esta fila SÍ es
        // reintentable (a diferencia de los otros conflicts): cuando hay lugar
        // disponible entra con "Reintentar".
        //
        // El texto dice "cuando tengas lugar" y no "cuando amplíes el plan" a
        // propósito: adentro de la app no va nada que empuje a comprar, que es
        // lo que la mantiene en regla con la guideline 3.1.3(d) de Apple. La
        // invitación a ver los planes sale sólo por mail
        // (SubscriptionMessages.llamadoALaAccion en el backend).
        if (errorCode === "plan_limit")
            return `${raw ?? "Alcanzaste el límite de tu plan."} Tu registro sigue guardado acá: cuando tengas lugar disponible, tocá Reintentar.`;
        if (!raw) return "";
        const s = String(raw).toLowerCase();
        if (
            s.includes("failed to fetch") ||
            s.includes("networkerror") ||
            s.includes("network request failed") ||
            s.includes("load failed")
        )
            return "Sin conexión con el servidor. Se reintentará cuando vuelva la señal.";
        if (s.includes("sesi") || s.includes("expirad") || s.includes("401"))
            return "Tu sesión venció. Volvé a iniciar sesión e intentá de nuevo.";
        if (s.includes("forbidden") || s.includes("permiso") || s.includes("403"))
            return "No tenés permiso para realizar esta acción.";
        if (s.includes("not_found") || s.includes("not found") || s.includes("404"))
            return "Un dato relacionado ya no existe en el servidor.";
        if (s.includes("establishment is required"))
            return "Faltó el establecimiento (error interno). Avisá a soporte.";
        return raw;
    }

    function statusMeta(s) {
        return STATUS_META[s] ?? { label: s, cls: "bg-gray-100 text-gray-700" };
    }

    // Una fila esta "en problema" (necesita decision del usuario) si error/conflict.
    function isProblem(s) {
        return s === "error" || s === "conflict";
    }

    // Un conflict deterministico (forbidden/not_found/bad_request) NO se puede
    // reintentar (reintentar no cambia nada): solo se descarta. El "error"
    // transitorio (red/5xx) sí se reintenta.
    function esReintentable(row) {
        if (row.status === "error") return true;
        return !["forbidden", "not_found", "bad_request"].includes(row.errorCode);
    }

    // Feedback inmediato al tocar "Sincronizar ahora": si no hay red avisamos en
    // vez de no hacer nada (el drainer igual no manda nada offline). Si hay red,
    // drenamos y la lista se actualiza sola via liveQuery.
    let syncMsg = $state("");
    async function syncNow() {
        if (!isOnline()) {
            syncMsg =
                "Sin conexión. Tus cambios se sincronizarán solos cuando vuelva la señal.";
            return;
        }
        syncMsg = "";
        await drainOutbox();
    }

    function close() {
        syncMsg = "";
        syncUi.closeDrawer();
    }

    function onKeydown(e) {
        if (e.key === "Escape") close();
    }
</script>

<svelte:window onkeydown={syncUi.open ? onKeydown : undefined} />

{#if syncUi.open}
    <!-- overlay -->
    <div
        class="fixed inset-0 z-[60] bg-black/40"
        transition:fade={{ duration: 150 }}
        onclick={close}
        role="presentation"
    ></div>

    <!-- panel -->
    <aside
        class="fixed inset-y-0 right-0 z-[61] flex w-full max-w-md flex-col bg-white shadow-2xl dark:bg-cf-surface dark:text-cf-text"
        transition:fly={{ x: 400, duration: 200 }}
        aria-label="Estado de sincronización"
    >
        <!-- El panel es fixed a pantalla completa y tapa la navbar, asi que se
             come sus propias safe-areas: sin esto el titulo queda debajo de la
             barra de estado y el boton de abajo debajo de la barra del sistema. -->
        <header
            class="flex items-center justify-between border-b border-gray-200 px-4 py-3 pt-[calc(0.75rem_+_var(--cf-safe-top))] border-cf-border"
        >
            <h2 class="text-base font-semibold">Sincronización</h2>
            <button
                type="button"
                onclick={close}
                class="rounded-full p-1 text-gray-500 hover:bg-cf-surface-alt"
                aria-label="Cerrar"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                    />
                </svg>
            </button>
        </header>

        <div class="flex-1 overflow-y-auto px-4 py-3">
            {#if !$rows}
                <p class="py-8 text-center text-sm text-gray-500">Cargando…</p>
            {:else if $rows.length === 0}
                <div class="py-12 text-center">
                    <p class="text-sm font-medium text-emerald-600">
                        Todo sincronizado
                    </p>
                    <p class="mt-1 text-xs text-gray-500">
                        No hay cambios esperando subir.
                    </p>
                </div>
            {:else}
                <ul class="space-y-2">
                    {#each $rows as row (row.id)}
                        {@const meta = statusMeta(row.status)}
                        <li
                            class="rounded-lg border border-gray-200 p-3 border-cf-border"
                        >
                            <div class="flex items-start justify-between gap-2">
                                <div>
                                    <p class="text-sm font-medium">
                                        {entityLabel(row.entity)}
                                    </p>
                                    <p class="text-xs text-gray-500">
                                        {hace(row.createdAt)}
                                    </p>
                                </div>
                                <span
                                    class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium {meta.cls}"
                                >
                                    {meta.label}
                                </span>
                            </div>

                            {#if isProblem(row.status) && row.lastError}
                                <p
                                    class="mt-2 rounded bg-cf-danger-soft px-2 py-1 text-xs text-cf-danger dark:bg-cf-danger dark:text-cf-danger"
                                >
                                    {humanizeError(row.lastError, row.errorCode)}
                                </p>
                            {/if}

                            {#if isProblem(row.status)}
                                <div class="mt-2 flex gap-2">
                                    {#if esReintentable(row)}
                                        <button
                                            type="button"
                                            onclick={() => retryOperation(row.id)}
                                            class="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                                        >
                                            Reintentar
                                        </button>
                                    {/if}
                                    <button
                                        type="button"
                                        onclick={() => discardOperation(row.id)}
                                        class="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 border-cf-border-strong dark:text-cf-text dark:hover:bg-cf-surface-alt"
                                    >
                                        Descartar
                                    </button>
                                </div>
                            {:else}
                                <div class="mt-2 flex items-center justify-between gap-2">
                                    <span class="text-xs text-gray-400">
                                        Se sincronizará automáticamente.
                                    </span>
                                    <button
                                        type="button"
                                        onclick={() => discardOperation(row.id)}
                                        class="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 border-cf-border-strong dark:text-cf-text dark:hover:bg-cf-surface-alt"
                                    >
                                        Descartar
                                    </button>
                                </div>
                            {/if}
                        </li>
                    {/each}
                </ul>
            {/if}
        </div>

        <footer
            class="border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem_+_var(--cf-safe-bottom))] border-cf-border"
        >
            {#if syncMsg}
                <p
                    class="mb-2 rounded bg-amber-50 px-2 py-1 text-center text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                >
                    {syncMsg}
                </p>
            {/if}
            <button
                type="button"
                onclick={syncNow}
                class="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
                Sincronizar ahora
            </button>
        </footer>
    </aside>
{/if}
